import { AddressType, Code, CodeStub, DecryptedAddress, DecryptedPatient, DecryptedTelecom, Gender, PersonName, PersonNameUse, TelecomType } from '@icure/cardinal-sdk'
import type { GetProps } from 'antd'
import { DatePicker, Form, Input, Select } from 'antd'
import dayjs from 'dayjs'
import React, { ReactElement, useEffect } from 'react'

import { useCreateOrUpdatePatientMutation } from '../../../../core/api/patientApi'
import { getPatientDataFormated } from '../../../../helpers/patientDataManipulations'
import { CustomModal } from '../../../common/CustomModal'
import './index.less'
import { SpinLoader } from '../../../common/SpinLoader'
import { allPatientsTagsEnum, PatientsTagsEnum } from '../../../../helpers/types'
import { DATE_FORMAT_TO_DISPLAY } from '../../../../constants'
import { useCreateOrUpdateCodesMutation, useGetCodesQuery } from '../../../../core/api/codeApi'
import { ModalConfirmAction } from '../../../common/ModalConfirmAction'

type PatientForm = {
  firstName: string
  lastName: string
  emailAddress: string
  phoneNumber: string
  dateOfBirth?: number
  ssin?: string
  birthSex: Gender
  language: string
  city: string
  street: string
  houseNumber: string
  postalCode: string
  tags: PatientsTagsEnum[]
}

interface ModalPatientFormProps {
  mode: 'edit' | 'create'
  isVisible: boolean
  onClose: () => void
  patientToEdit?: DecryptedPatient
}

type RangePickerProps = GetProps<typeof DatePicker.RangePicker>

export const ModalPatientForm = ({ mode, isVisible, onClose, patientToEdit }: ModalPatientFormProps): ReactElement => {
  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    // Can not select days before today and today
    return current && current > dayjs().endOf('day')
  }
  const [form] = Form.useForm()

  const [
    createOrUpdatePatient,
    { error: createOrUpdatePatientError, isError: isCreateOrUpdatePatientError, isSuccess: isPatientCreatedOrUpdateSuccessfully, isLoading: isPatientCreatingOrUpdatingLoading },
  ] = useCreateOrUpdatePatientMutation()

  const [createCodes] = useCreateOrUpdateCodesMutation()
  const [codesToCreate, setCodesToCreate] = React.useState<string[]>([])

  const { data: customTags } = useGetCodesQuery('PREVENTI')

  if (isCreateOrUpdatePatientError) {
    console.log(createOrUpdatePatientError)
  }

  const getPreparedUserData = (value: PatientForm) => {
    const { firstName, lastName, phoneNumber, emailAddress, dateOfBirth, birthSex, tags, city, street, houseNumber, postalCode, language, ssin } = value
    const name = new PersonName({
      firstNames: [firstName],
      lastName: lastName,
      use: PersonNameUse.Official,
    })
    const dob = dateOfBirth ? dayjs(dateOfBirth) : undefined
    const fuzzyDateOfBirth = dob ? (dob.year() * 100 + dob.month() + 1) * 100 + dob.date() : undefined

    const address = new DecryptedAddress({
      addressType: AddressType.Home,
      telecoms: [
        ...(emailAddress
          ? [
              new DecryptedTelecom({
                telecomType: TelecomType.Email,
                telecomNumber: emailAddress,
              }),
            ]
          : []),
        ...(phoneNumber
          ? [
              new DecryptedTelecom({
                telecomType: TelecomType.Mobile,
                telecomNumber: phoneNumber,
              }),
            ]
          : []),
      ],
      city,
      street,
      houseNumber,
      postalCode,
    })
    const getTags = () => {
      return (tags ?? []).map((tag) => {
        const tagType = 'PREVENTI'
        const tagVersion = '1'

        return new CodeStub({
          id: `${tagType}|${tag}|${tagVersion}`,
          type: tagType,
          code: tag,
          version: tagVersion,
        })
      })
    }

    return { firstName, lastName, names: [name], dateOfBirth: fuzzyDateOfBirth, addresses: [address], tags: getTags(), birthSex, languages: language ? [language] : [], ssin }
  }
  const handleSubmit = (value: PatientForm) => {
    if (mode === 'create') createOrUpdatePatient(new DecryptedPatient(getPreparedUserData(value)))
    if (mode === 'edit') createOrUpdatePatient(new DecryptedPatient({ ...patientToEdit, ...getPreparedUserData(value) }))

    form.resetFields()
  }
  const getPatientToEdit = (patient: DecryptedPatient) => {
    const { firstName, lastName, dateOfBirth, ssin, phoneNumber, emailAddress, tags, birthSex, language, city, street, houseNumber, postalCode } = getPatientDataFormated(patient)
    return {
      firstName,
      lastName,
      dateOfBirth: dateOfBirth ? dayjs(dateOfBirth.toString().replace(/(....)(..)(..)/, '$1-$2-$3')) : undefined,
      ssin,
      phoneNumber,
      emailAddress,
      birthSex,
      language,
      city,
      street,
      houseNumber,
      postalCode,
      tags: tags?.map((tag) => tag.code) ?? [],
    }
  }

  useEffect(() => {
    if (isPatientCreatedOrUpdateSuccessfully) {
      onClose()
    }
  }, [isPatientCreatedOrUpdateSuccessfully])

  const handleTagChange = (value: string[]) => {
    //extract unknown tags from value
    const unknownTags = value.filter((tag) => !allPatientsTagsEnum.includes(tag as PatientsTagsEnum) && !customTags?.some((customTag) => customTag.code === tag))

    //if there are unknown tags, add them to custom tags
    if (unknownTags.length > 0) {
      form.setFieldValue(
        'tags',
        value.filter((tag) => !unknownTags.includes(tag)),
      )
      setCodesToCreate(unknownTags)
    }
  }

  const handleOnClose = () => {
    form.resetFields()
    onClose()
  }

  const { Option } = Select

  return (
    <>
      <CustomModal
        isVisible={isVisible}
        handleClose={handleOnClose}
        secondaryBtnTitle="Cancel"
        handleClickPrimaryBtn={() => form.submit()}
        primaryBtnTitle="Save"
        title={mode === 'edit' ? 'Edit patient' : 'Create patient'}
      >
        <div className="modalPatientForm">
          {isPatientCreatingOrUpdatingLoading && <SpinLoader />}
          <Form
            className="modalPatientForm__form"
            layout="vertical"
            colon={false}
            form={form}
            onFinish={(values) => handleSubmit(values)}
            initialValues={mode === 'edit' && patientToEdit ? getPatientToEdit(patientToEdit) : {}}
          >
            <div className="modalPatientForm__form__infoBlock">
              <h3 className="modalPatientForm__form__infoBlock__title">Main information:</h3>
              <div className="modalPatientForm__form__infoBlock__inputs">
                <Form.Item name="firstName" label="First name" rules={[{ required: true, message: 'First name is required' }]}>
                  <Input placeholder="First name" size="large" />
                </Form.Item>
                <Form.Item name="lastName" label="Last name" rules={[{ required: true, message: 'Last name is required' }]}>
                  <Input placeholder="Last name" size="large" />
                </Form.Item>
                <Form.Item name="dateOfBirth" label="Date of birth">
                  <DatePicker format={DATE_FORMAT_TO_DISPLAY} placeholder="Date of birth" size="large" style={{ width: '100%' }} disabledDate={disabledDate} />
                </Form.Item>
                <Form.Item name="ssin" label="SSIN">
                  <Input placeholder="SSIN" size="large" />
                </Form.Item>
                <Form.Item name="birthSex" label="Sex at birth">
                  <Select placeholder="Sex at birth" size="large">
                    <Option value={Gender.Male}>{Gender.Male}</Option>
                    <Option value={Gender.Female}>{Gender.Female}</Option>
                  </Select>
                </Form.Item>
                <Form.Item name="language" label="Language">
                  <Select placeholder="Language" size="large">
                    <Option value={'fr'}>Français</Option>
                    <Option value={'nl'}>Néerlandais</Option>
                  </Select>
                </Form.Item>
              </div>
            </div>
            <div className="modalPatientForm__form__infoBlock">
              <h3 className="modalPatientForm__form__infoBlock__title">Contacts:</h3>
              <div className="modalPatientForm__form__infoBlock__inputs">
                <Form.Item name="phoneNumber" label="Phone number">
                  <Input placeholder="Phone number" size="large" />
                </Form.Item>
                <Form.Item name="emailAddress" label="Email address">
                  <Input placeholder="Email address" size="large" />
                </Form.Item>
              </div>
            </div>
            <div className="modalPatientForm__form__infoBlock">
              <h3 className="modalPatientForm__form__infoBlock__title">Home address:</h3>
              <div className="modalPatientForm__form__infoBlock__inputs">
                <Form.Item name="city" label="City">
                  <Input placeholder="City" size="large" />
                </Form.Item>
                <Form.Item name="street" label="Street">
                  <Input placeholder="Street" size="large" />
                </Form.Item>
                <Form.Item name="houseNumber" label="House number">
                  <Input placeholder="House number" size="large" />
                </Form.Item>
                <Form.Item name="postalCode" label="Postal code">
                  <Input placeholder="Postal code" size="large" />
                </Form.Item>
              </div>
            </div>
            <div className="modalPatientForm__form__infoBlock">
              <h3 className="modalPatientForm__form__infoBlock__title">Tags:</h3>
              <div className="modalPatientForm__form__infoBlock__inputs">
                <Form.Item name="tags" label="Tags">
                  <Select
                    placeholder="Tags"
                    size="large"
                    mode="tags"
                    onChange={handleTagChange}
                    options={[
                      {
                        label: <span>Workflow</span>,
                        title: 'workflow',
                        options: allPatientsTagsEnum.map((tag) => ({ label: <span>{tag}</span>, value: tag })),
                      },
                      {
                        label: <span>Custom tags</span>,
                        title: 'custom tags',
                        options: (customTags ?? []).map((tag) => ({ label: <span>{tag.code}</span>, value: tag.code })),
                      },
                    ]}
                  />
                </Form.Item>
              </div>
            </div>
          </Form>
        </div>
      </CustomModal>
      <ModalConfirmAction
        title="Create custom tag"
        description={
          <span>
            You are about to create the custom tag{codesToCreate.length > 1 ? 's' : ''} <strong>{codesToCreate.join(', ')}</strong>. Do you want to proceed?
          </span>
        }
        isVisible={codesToCreate.length > 0}
        onYesClick={() => {
          createCodes(codesToCreate.map((tag) => new Code({ id: `PREVENTI|${tag}|1`, code: tag, type: 'PREVENTI', version: '1' })))
          form.setFieldValue('tags', [...(form.getFieldValue('tags') as string[]), ...codesToCreate])
          setCodesToCreate([])
        }}
        onNoClick={() => setCodesToCreate([])}
        yesBtnTitle="Ok"
        noBtnTitle="Cancel"
      />
    </>
  )
}
